import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/lilia-ui/button";
import { Input } from "@/components/lilia-ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/lilia-ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/lilia-ui/dialog";
import { useCreateTemplate } from "@/hooks/useTemplates";
import { useToast } from "@/components/shared/Toast";
import { testIds } from "@/testIds";
import { cn } from "@/lib/cn";
import {
  TEMPLATE_CATEGORIES, NO_CATEGORY, initialDraft, templateRequest,
  type TemplateDraft, type TemplateVisibility,
} from "./templateForm";

const VISIBILITY: { value: TemplateVisibility; label: string; hint: string }[] = [
  { value: "private", label: "Private", hint: "Only you see it in the gallery." },
  { value: "public", label: "Public", hint: "Anyone can find and use it." },
];

const LABEL = "block mb-1.5 font-medium text-[13px] text-[var(--lilia-text)]";

/**
 * Save as template (templates handoff, 27 Sep 2026, §1–2). One form in two
 * places: the editor's ⋯ opens it on the open document, the gallery's Create
 * template opens it on the document picked there. The template is a copy, so
 * it belongs to whoever saves it; the API copies what Duplicate copies and
 * never the collaborators.
 */
export function SaveAsTemplateDialog({
  open, onOpenChange, documentId, documentTitle,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentId: string | null;
  documentTitle: string;
}) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const createTemplate = useCreateTemplate();
  const [draft, setDraft] = useState<TemplateDraft>(() => initialDraft(documentTitle));
  const [error, setError] = useState<string | null>(null);

  // Each opening starts from the document as it is now — not on every title
  // change while the dialog is open, which would wipe what the author typed.
  useEffect(() => {
    if (!open) return;
    setDraft(initialDraft(documentTitle));
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, documentId]);

  const request = documentId ? templateRequest(documentId, draft) : null;
  const saving = createTemplate.isPending;

  const save = async () => {
    if (!request || saving) return;
    setError(null);
    try {
      await createTemplate.mutateAsync(request);
      onOpenChange(false);
      showToast("Saved as template", "success", {
        action: { key: "view", label: "View", onClick: () => navigate("/templates") },
      });
    } catch {
      setError("Couldn't save the template. Try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!saving) onOpenChange(next); }}>
      <DialogContent data-testid={testIds.templates.saveDialog} className="max-w-md">
        <DialogHeader>
          <DialogTitle>Save as template</DialogTitle>
          <DialogDescription className="text-[13px] text-[var(--lilia-text-muted)]">
            A copy of the content and its settings. Collaborators are not included.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => { e.preventDefault(); void save(); }}
        >
          <div>
            <label htmlFor="save-template-name" className={LABEL}>Name</label>
            <Input
              id="save-template-name"
              data-testid={testIds.templates.saveName}
              value={draft.name}
              autoFocus
              onChange={(e) => { const name = e.currentTarget.value; setDraft((d) => ({ ...d, name })); }}
            />
          </div>

          <div>
            <label htmlFor="save-template-category" className={LABEL}>
              Category <span className="font-normal text-[var(--lilia-text-muted)]">(optional)</span>
            </label>
            <Select value={draft.category} onValueChange={(v) => setDraft((d) => ({ ...d, category: v || NO_CATEGORY }))}>
              <SelectTrigger id="save-template-category" data-testid={testIds.templates.saveCategory}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CATEGORY} data-testid={testIds.templates.saveCategoryOption} data-value={NO_CATEGORY}>
                  No category
                </SelectItem>
                {TEMPLATE_CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value} data-testid={testIds.templates.saveCategoryOption} data-value={c.value}>
                    {c.text}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <fieldset>
            <legend className={LABEL}>Visibility</legend>
            <div role="radiogroup" aria-label="Visibility" data-testid={testIds.templates.saveVisibility} className="grid grid-cols-2 gap-2">
              {VISIBILITY.map((v) => {
                const on = draft.visibility === v.value;
                return (
                  <button
                    key={v.value}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    data-testid={testIds.templates.saveVisibilityOption}
                    data-value={v.value}
                    onClick={() => setDraft((d) => ({ ...d, visibility: v.value }))}
                    className={cn(
                      "rounded-lg border px-3 py-2 text-left transition-colors",
                      on
                        ? "border-[var(--lilia-primary)] bg-[var(--lilia-primary-subtle)]"
                        : "border-[var(--lilia-border-strong)] hover:bg-[var(--lilia-hover)]",
                    )}
                  >
                    <span className="block text-[13px] font-medium text-[var(--lilia-text)]">{v.label}</span>
                    <span className="block text-[12px] text-[var(--lilia-text-muted)]">{v.hint}</span>
                  </button>
                );
              })}
            </div>
          </fieldset>

          {error && (
            <p role="alert" data-testid={testIds.templates.saveError} className="m-0 text-[13px] text-[var(--lilia-danger,#dc2626)]">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" data-testid={testIds.templates.saveCancel} disabled={saving} onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" data-testid={testIds.templates.saveSubmit} disabled={!request || saving}>
              {saving ? "Saving…" : "Save template"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
